import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useClientTable } from "../table/tableFeatures";
import { usePersistedTableState } from "../table/usePersistedTableState";
import { useAppSelector } from "../../redux/hooks";
import { apiClient } from "../../zodios/client";
import { HStack, VStack } from "../../../styled-system/jsx";
import { Button } from "../core/basic/Button";
import { resetTable } from "../table/utils";
import { customFilterFn } from "../table/filterFns";
import { MdRotateLeft } from "react-icons/md";
import { RT8ColumnSelector } from "../core/blocks/MetricSelector";
import { SearchInput } from "../fields/base/TextField";
import { css } from "../../../styled-system/css";
import { RT8FilterWidget } from "../../modules/merchandising/components/tables/FilterWidget";
import { RT8Pagination } from "../table/Pagination";
import { Table } from "../table/Table";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { HiddenField, InputField } from "./programs/fields";
import { Form } from "react-bootstrap";
import { formatCurrency, getCurrencySymbol } from "../../utils/formatNumber";
import { successToast } from "../../utils/toasts";

const PortfolioForm = ({ portfolioId, scheduledAction = null }) => {
  const { t } = useTranslation(["advertising", "common"]);
  const queryClient = useQueryClient();
  const form = useForm({
    defaultValues: {
      enableBudgetSchedule: scheduledAction?.ui_state?.enableBudgetSchedule ?? false,
      schedule: scheduledAction?.ui_state?.schedule ?? {
        type: "daily",
        amount: 0,
      },
    },
  });
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const currencySymbol = getCurrencySymbol(marketPlace);
  const enableBudgetSchedule = useWatch({
    control: form.control,
    name: "enableBudgetSchedule",
  });

  const updateScheduledAction = useMutation({
    mutationFn: async ({ id, ui_state }) => {
      const cron = ui_state.schedule?.type === "daily" ? "0 0 * * *" : null;
      const body = {
        id,
        ui_state,
        is_enabled: ui_state.enableBudgetSchedule,
        cron,
        action_type: "update_portfolio_budget",
        start_date: scheduledAction?.start_date || new Date().toISOString(),
        end_date: scheduledAction?.end_date || null,
      };
      return await apiClient.updateScheduleSyncAction(body, {
        params: { id },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["scheduledSyncAction", "portfolio"],
      });
      successToast({ message: t(($) => $.advertising["Portfolio Budget Schedule updated successfully."]) });
    },
  });

  const createScheduledAction = useMutation({
    mutationFn: async (ui_state) => {
      const cron = ui_state.schedule?.type === "daily" ? "0 0 * * *" : null;
      const body = {
        ui_state,
        content_type: "gvportfolio",
        entity_id: portfolioId,
        is_enabled: ui_state.enableBudgetSchedule,
        cron,
        action_type: "update_portfolio_budget",
        start_date: scheduledAction?.start_date || new Date().toISOString(),
        end_date: scheduledAction?.end_date || null,
      };
      return await apiClient.createScheduleSyncAction(body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["scheduledSyncAction", "portfolio"],
      });
      successToast({ message: t(($) => $.advertising["Portfolio Budget Schedule updated successfully."]) });
    },
  });

  const onSubmit = (data) => {
    const ui_state = data;
    if (scheduledAction) {
      updateScheduledAction.mutate({ id: scheduledAction.id, ui_state });
    } else {
      createScheduledAction.mutate(ui_state);
    }
  };

  return (
    <form
      className={css({ maxWidth: "400px", padding: "16px" })}
      onSubmit={form.handleSubmit((data) => {
        onSubmit(data);
      })}
    >
      <Form.Check
        type="checkbox"
        label={t(($) => $.advertising["Enable Daily Budget Schedule"])}
        {...form.register("enableBudgetSchedule")}
      />
      <HiddenField
        control={form.control}
        fieldName={"schedule.type"}
        defaultValue={"daily"}
        formState={form.formState}
      />
      <InputField
        disabled={!enableBudgetSchedule}
        label={t(($) => $.advertising["Budget Amount"])}
        type="number"
        control={form.control}
        fieldName={"schedule.amount"}
        formState={form.formState}
        symbol={currencySymbol}
      />
      <Button
        isDisabled={createScheduledAction?.isPending || updateScheduledAction.isPending}
        type="submit"
        variant="primary"
        className={css({ marginTop: "16px" })}
      >
        {t(($) => $.common.actions["Save"])}
      </Button>
    </form>
  );
};

const PortfolioFormWrapper = ({ portfolioId }) => {
  const { data: scheduledSyncAction, isLoading } = useQuery({
    queryKey: ["scheduledSyncAction", "portfolio", portfolioId],
    queryFn: () =>
      apiClient.getScheduleSyncActions({ queries: { entity_id: portfolioId, content_type__model: "gvportfolio" } }),
    options: {
      refetchOnMount: true,
      enabled: !!portfolioId,
    },
  });

  if (isLoading) {
    return <div>Loading...</div>;
  }

  const scheduledAction = scheduledSyncAction?.results?.[0];

  return <PortfolioForm scheduledAction={scheduledAction} portfolioId={portfolioId} />;
};

const dateSortingFn = (rowA, rowB, columnId) => {
  const a = rowA.getValue(columnId);
  const b = rowB.getValue(columnId);
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return (
    a < b ? -1
    : a > b ? 1
    : 0
  );
};

const PortfoliosTable = () => {
  const { t } = useTranslation(["advertising", "common"]);
  const { marketPlace } = useAppSelector((state) => state.marketPlace);
  const defaultSelectedColumns = ["portfolio_name"];
  const { setters, state, initialState } = usePersistedTableState({
    persistKey: "portfolios",
    initialState: {
      columnOrder: defaultSelectedColumns,
      columnPinning: {
        start: ["portfolio_name"],
        end: [],
      },
      sorting: [{ id: "portfolio_name" }],
    },
  });

  // Backend only supports sorting/filtering by name, so fetch the full marketplace list and let the
  // table sort/filter/paginate client-side.
  const portfolios = useQuery({
    queryKey: ["portfolios", marketPlace],
    queryFn: () => {
      const queries = {
        limit: 10000,
        market_place: marketPlace,
      };
      return apiClient.getPortfolios({
        queries,
      });
    },
    options: {
      refetchOnMount: true,
    },
  });

  const columns = [
    { accessorKey: "portfolio_name", header: t(($) => $.advertising["Name"]) },
    {
      accessorKey: "portfolio_state",
      header: t(($) => $.advertising["State"]),
      meta: { columnFilterOptions: { format: "single-select" } },
    },
    {
      accessorKey: "portfolio_budget_amount",
      header: t(($) => $.advertising["Budget Amount"]),
      cell: ({ getValue }) => formatCurrency(getValue(), { marketPlace }),
    },
    { accessorKey: "portfolio_budget_type", header: t(($) => $.advertising["Budget Type"]) },
    {
      id: "portfolio_budget_start_date",
      accessorFn: (row) => row.portfolio_budget_start_date ?? undefined,
      header: t(($) => $.advertising["Start Date"]),
      sortFn: dateSortingFn,
      sortUndefined: "last",
      meta: { columnFilterOptions: { format: "date" } },
    },
    {
      id: "portfolio_budget_end_date",
      accessorFn: (row) => row.portfolio_budget_end_date ?? undefined,
      header: t(($) => $.advertising["End Date"]),
      sortFn: dateSortingFn,
      sortUndefined: "last",
      meta: { columnFilterOptions: { format: "date" } },
    },
  ];

  const table = useClientTable({
    data: portfolios.data?.results,
    columns,
    defaultColumn: {
      filterFn: customFilterFn,
    },
    getRowCanExpand: () => true,
    getRowId: (row) => row.portfolio_id?.toString?.() ?? row.id?.toString?.(),
    enableGlobalFilter: true,
    initialState: initialState,
    state: state,
    ...setters,
    meta: {
      showSkeleton: portfolios.isFetching && (!portfolios.data || portfolios.isPlaceholderData),
      columnFilterSelectOptions: {
        portfolio_state: [
          { label: t(($) => $.advertising["Enabled"]), value: "enabled" },
          { label: t(($) => $.advertising["Disabled"]), value: "disabled" },
        ],
      },
    },
  });
  return (
    <>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button
              variant={"contentOnly"}
              onPress={() => {
                resetTable(table);
              }}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <SearchInput
              aria-label={t(($) => $.common.actions["Search"])}
              isExpandable
              css={{ flex: "0 1 350px" }}
              value={table.state.globalFilter}
              onChange={table.setGlobalFilter}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table
        table={table}
        className={css({ minWidth: "100%" })}
        renderExpander={(row) => {
          return <PortfolioFormWrapper portfolioId={row.id} />;
        }}
      />
    </>
  );
};

const AmazonPortfolios = () => {
  return (
    <div>
      <PortfoliosTable />
    </div>
  );
};

export default AmazonPortfolios;
